/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { Link, useRouterState } from '@tanstack/react-router'
import type { MouseEvent, ReactElement } from 'react'
import { forwardRef, useMemo } from 'react'

import { NavBadge } from '@/components/blocks/nav-badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { ACTIVE_BAR_CLASS, LABEL_COLLAPSED_TRANSITION, LABEL_EXPANDED_TRANSITION } from './side-nav-constants'
import { useSideNavContext } from './side-nav-context'

export interface SideNavItemProps {
  icon: ReactElement
  label: string
  to?: string
  onClick?: (event: MouseEvent) => void
  badge?: number | boolean
  isActive?: boolean | ((location: { pathname: string }) => boolean)
  inactiveBgClassName?: string
  activeBgClassName?: string
  hoverBgClassName?: string
  colorClassName?: string
  hoverColorClassName?: string
}

export const SideNavItem = forwardRef<HTMLDivElement, SideNavItemProps>(function SideNavItem(props, ref) {
  const {
    icon,
    label,
    to,
    onClick,
    badge,
    isActive: isActiveProp,
    hoverBgClassName = 'hover:bg-card',
    colorClassName = 'text-muted-foreground',
    hoverColorClassName = 'hover:text-foreground',
  } = props
  const { expanded, side } = useSideNavContext()
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  const active = useMemo(() => {
    if (typeof isActiveProp === 'boolean') {
      return isActiveProp
    }
    if (typeof isActiveProp === 'function') {
      return isActiveProp({ pathname: currentPath })
    }
    if (to) {
      return currentPath === to || currentPath.startsWith(`${to}/`)
    }
    return false
  }, [isActiveProp, currentPath, to])

  const itemInner = (
    <div
      className={cn(
        'relative flex min-h-10 cursor-pointer items-center overflow-hidden rounded-md transition-colors',
        expanded ? 'mx-2 gap-3 px-3' : 'w-full justify-center px-0',
        hoverBgClassName,
        active ? 'text-foreground' : colorClassName,
        !active && hoverColorClassName
      )}
      ref={ref}
    >
      <div className="relative flex shrink-0 items-center gap-2 [&_svg]:size-6">
        {expanded ? (
          <span
            aria-hidden
            className={cn('h-7 w-0.5 shrink-0 self-center rounded-sm', active ? ACTIVE_BAR_CLASS : 'bg-transparent')}
          />
        ) : null}
        <span className="inline-flex">{icon}</span>
        <NavBadge badge={badge} />
      </div>
      <div
        className="min-w-0 overflow-hidden"
        style={{
          maxWidth: expanded ? undefined : '0px',
          opacity: expanded ? 1 : 0,
          transition: expanded ? LABEL_EXPANDED_TRANSITION : LABEL_COLLAPSED_TRANSITION,
        }}
      >
        <span className={cn('block truncate text-lg md:text-base', active ? 'font-semibold' : 'font-medium')}>
          {label}
        </span>
      </div>
    </div>
  )

  const tooltipPlacement = side === 'left' ? 'right' : 'left'

  if (to) {
    if (!expanded) {
      return (
        <Tooltip>
          <TooltipTrigger render={<Link className="block no-underline" onClick={onClick} to={to} />}>
            {itemInner}
          </TooltipTrigger>
          <TooltipContent align="center" side={tooltipPlacement}>
            {label}
          </TooltipContent>
        </Tooltip>
      )
    }

    return (
      <Link className="block no-underline" onClick={onClick} to={to}>
        {itemInner}
      </Link>
    )
  }

  if (!expanded) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <button className="block w-full border-none bg-transparent p-0 text-left" onClick={onClick} type="button" />
          }
        >
          {itemInner}
        </TooltipTrigger>
        <TooltipContent align="center" side={tooltipPlacement}>
          {label}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <button className="block w-full border-none bg-transparent p-0 text-left" onClick={onClick} type="button">
      {itemInner}
    </button>
  )
})

SideNavItem.displayName = 'SideNavItem'
